<!-- Responsibility: Implement item card behavior for the ui/components surface.
Scope: Owns ui/components concerns only; cross-surface orchestration is excluded. -->

<item-card>
  <button
    class="item-card tooltip-wrap { isCompact() ? 'compact' : '' } { state.selected ? 'selected' : '' }"
    style="--accent-color: { props.accentColor ?? 'var(--chip-item-border)' }"
    type="button"
    aria-label={ ariaLabel() }
    data-tip-lock={ props.showTooltip === false ? 'off' : null }
    onclick={ openDetails }
    onkeydown={ onKeydown }
  >
    <header>
      <object-visual name={ props.name } size={ visualSize() } icon-only={true}></object-visual>
      <div class="identity">
        <span class="name">{ compactName() }</span>
        <span if={ isCompact() && statusBadgeText() } class="badge { isEquippedStatus() ? 'equipped' : '' }">
          { statusBadgeText() }
        </span>
        <span class="status-meta" if={ !isCompact() && statusBadgeText() }>{ statusBadgeText() }</span>
      </div>
    </header>

    <footer if={ !isCompact() }>
      <p>{ detailLine() }</p>
      <span class="action-hint">INSPECT</span>
    </footer>

    <!-- HTML-First Tooltip -->
    <div class="tooltip" data-position="top" if={ shouldRenderCompactTooltip() } data-tip-custom="true">
      <b>{ props.name }</b><br/>
      { statusBadgeText() }
    </div>
  </button>

  <script>
    import ObjectVisual from './object-visual.riot'
    import { dispatcher } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { getItemStatusInfo } from '../helpers/item-status.js'
    import { openItemDialog } from '../dialog/dialog-openers.ts'

    export default {
      components: { ObjectVisual },
      state: { selected: false },

      onMounted() {
        this.modalCloseHandle = dispatcher.on(TOPICS.ui.dialogClose, () => this.clearSelected())
      },

      onUnmounted() {
        if (this.modalCloseHandle) dispatcher.off(this.modalCloseHandle)
        if (this.selectedTimer) clearTimeout(this.selectedTimer)
      },

      get statusInfo() {
        return getItemStatusInfo(
          this.props.name,
          this.props.location ?? 'room',
          this.props.equipped ?? false,
        )
      },

      detailLine() {
        const detail = String(this.statusInfo?.detail || '').trim()
        if (!detail) return 'No status details.'
        return detail.length > 60 ? `${detail.slice(0, 57)}...` : detail
      },

      isCompact() {
        return this.props.compact === true || this.props.compact === 'true'
      },

      shouldRenderCompactTooltip() {
        return this.isCompact() && this.props.showTooltip !== false
      },

      visualSize() {
        return this.isCompact() ? 'sm' : 'lg'
      },

      middleTruncate(value, max = 18) {
        const text = String(value ?? '').trim()
        if (!text) return ''
        if (text.length <= max) return text
        const visible = Math.max(4, max - 1)
        const head = Math.ceil(visible / 2)
        const tail = Math.floor(visible / 2)
        return `${text.slice(0, head)}…${text.slice(-tail)}`
      },

      compactName() {
        const label = String(this.props.name ?? '').trim() || 'Item'
        return this.isCompact() ? this.middleTruncate(label, 18) : label
      },

      statusBadgeText() {
        const detail = String(this.statusInfo?.detail ?? '').toLowerCase()
        const labels = []
        if (detail.includes('equipped')) labels.push('Equipped')
        else if (detail.includes('stored')) labels.push('Stored')
        else if (detail.includes('inventory')) labels.push('Carried')
        else if (detail.includes('present in this room')) labels.push('In Room')
        if (detail.includes('damaged')) labels.push('Damaged')
        return labels.join(' · ')
      },

      isEquippedStatus() {
        return this.statusBadgeText().includes('Equipped')
      },

      ariaLabel() {
        const name = String(this.props.name ?? 'Item').trim() || 'Item'
        const status = this.statusBadgeText() || 'Unknown'
        return `Inspect item ${name}. Status ${status}. Action opens item details.`
      },

      markSelected() {
        if (this.selectedTimer) clearTimeout(this.selectedTimer)
        this.update({ selected: true })
        this.selectedTimer = setTimeout(() => this.update({ selected: false }), 2400)
      },

      clearSelected() {
        if (this.selectedTimer) clearTimeout(this.selectedTimer)
        this.update({ selected: false })
      },

      async openDetails() {
        this.markSelected()
        const name     = this.props.name
        const location = this.props.location ?? 'room'
        const equipped = this.props.equipped ?? false
        const statusInfo = getItemStatusInfo(name, location, equipped)
        await openItemDialog('item-card', name, {
          desc: statusInfo.detail,
          location,
          equipped,
        })
      },

      onKeydown(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          this.openDetails()
        }
      },
    }
  </script>

  <style>
    .item-card {
      position: relative;
      display: flex;
      flex-direction: column;
      background: var(--grad-main-bg);
      appearance: none;
      border: 1px solid var(--border);
      border-left: 0.25rem solid var(--accent-color);
      border-radius: 0.5rem;
      padding: 0.75rem;
      gap: 0.5rem;
      cursor: pointer;
      text-align: left;
      transition: all 0.16s ease;
      box-shadow: var(--shadow-elev-1);
      min-width: 0; /* Contain children */
    }

    .item-card:hover {
      transform: translateY(-1px);
      box-shadow: var(--shadow-elev-2);
      border-color: var(--alpha-mid);
    }

    .item-card.selected {
      border-color: var(--gold);
      box-shadow: var(--shadow-elev-2), 0 0 0 1px var(--gold-glow);
    }

    /* Compact Specifics */
    .item-card.compact {
      max-width: clamp(5.5rem, 15vw, 7.4rem);
      height: var(--room-chip-height);
      padding: 0.4rem 0.75rem;
      justify-content: center;
      border-radius: var(--radius-sm);
    }

    /* Header Logic (Icon + Text) */
    .item-card > header {
      display: grid;
      grid-template-columns: min-content 1fr;
      gap: 0.75rem;
      align-items: center;
      min-width: 0;
    }

    .identity {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      min-width: 0;
    }

    .identity .name {
      font-size: 1em;
      font-weight: 700;
      color: var(--text-prose);
      line-height: 1.25;
      flex: 1 1 auto;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .status-meta {
      font-size: 0.6rem;
      font-weight: 800;
      letter-spacing: 0.05em;
      color: var(--gold);
      opacity: 0.8;
    }

    .badge {
      display: inline-flex;
      align-items: center;
      gap: 0.2rem;
      max-width: 100%;
      border: 1px solid color-mix(in srgb, var(--chip-item-border) 42%, transparent);
      border-radius: var(--radius-pill);
      padding: 0.05rem 0.38rem;
      font-size: 0.6rem;
      letter-spacing: 0.04em;
      color: var(--chip-item-fg);
      background: color-mix(in srgb, var(--chip-item-surface-a) 72%, transparent);
    }

    .badge.equipped {
      border-color: color-mix(in srgb, var(--gold) 46%, transparent);
      color: color-mix(in srgb, var(--gold) 82%, #fff);
    }

    /* Footer Logic */
    .item-card > footer {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      gap: 1rem;
      margin-top: 0.25rem;
      border-top: 1px solid var(--border-subtle);
      padding-top: 0.5rem;
    }

    .item-card > footer p {
      margin: 0;
      font-size: 0.75rem;
      line-height: 1.4;
      color: var(--text-muted);
      flex: 1;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .action-hint {
      font-size: 0.6rem;
      font-weight: 900;
      color: var(--gold);
      letter-spacing: 0.05em;
    }

    @media (max-width: 30rem) {
      .item-card:not(.compact) > footer {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
      }
    }
  </style>
</item-card>
